import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

import {
  CustomerProfileDialog,
  UserManagementDialog,
  UserProfileDialog,
  LoadingIndicator,
  FadeIn,
} from "../../../elements/frontend/src/components";
import { SecondaryDialog } from "../../../elements/frontend/src/components";
import { useAccounts } from "../../../elements/frontend/src/hooks";
import {
  Wrapper,
  User,
  CompanyName,
  UserName,
  ListWrapper,
  List,
  ListItem,
} from "./styled.usermenu";

import TConfig from "../../../config";
import { setCurrentUser } from "../../../elements/frontend/src/Store/currentUser/currentUserSlice";

const UserMenu = () => {
  const { t } = useTranslation();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const currentUser = useSelector(({ currentUser }) => currentUser);
  const { deleteAccount } = useAccounts();

  const [isCustomerProfileDialogOpen, setIsCustomerProfileDialogOpen] = useState(false);
  const [isUserManagementDialogOpen, setIsUserManagementDialogOpen] = useState(false);
  const [isUserProfileDialogOpen, setIsUserProfileDialogOpen] = useState(false);
  const [isConfirmDeleteAccountDialogOpen, setIsConfirmDeleteAccountDialogOpen] = useState(false);


  // check whether it is advisor client
  const isAdvisorClient = currentUser.user?.client_id;

  // for advisor client, we show client name, not customer name
  const getAdvisorClientName = () => {
    const client = currentUser?.clients?.find(
      (client) => client.client_id === currentUser?.selectedClient?.client_id
    );
    return client.name;
  };

  const companyName = isAdvisorClient ? getAdvisorClientName() : currentUser.customer.name;
  const first_name = currentUser.user.first_name ? currentUser.user.first_name : "";
  const last_name = currentUser.user.last_name ? currentUser.user.last_name : "";
  const userName = first_name && last_name ? `${first_name} ${last_name}` : currentUser.user.alias;

  const dispatch = useDispatch();

  const menuRef = useRef(null);

  useEffect(() => {
    const handlerMenuVisible = (e) => {
      if (!menuRef.current.contains(e.target)) {
        setIsMenuVisible(false);
      }
    };

    if (isMenuVisible) {
      document.addEventListener("mousedown", handlerMenuVisible);

      return () => {
        document.removeEventListener("mousedown", handlerMenuVisible);
      };
    }
  });

  const handleToggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const handleLogOut = () => {
    navigate("/logout");
  };

  //to close delete dialog
  const handleDeleteDialogClose = () => {
    setIsConfirmDeleteAccountDialogOpen(false);
  };
  // for delete account with logout
  const handleDeleteAccount = () => {
    setIsConfirmDeleteAccountDialogOpen(false);
    dispatch(setCurrentUser({ loading: true }));
    deleteAccount()
      .then(() => {
        handleLogOut();
      })
      .catch(() => {
        dispatch(setCurrentUser({ loading: false }));
      });
  };

  const handleAccountDeleteDialog = () => {
    setIsMenuVisible(false);
    setIsConfirmDeleteAccountDialogOpen(true);
    setIsUserManagementDialogOpen(false);
    setIsCustomerProfileDialogOpen(false);
 };
  // for customerProfile Dialog
  const handleCustomerProfileDialogClose = () => {
    setIsCustomerProfileDialogOpen(false);
  };
  const handleCustomerProfileDialog = () => {
    setIsMenuVisible(false);
    setIsCustomerProfileDialogOpen(true);
    setIsConfirmDeleteAccountDialogOpen(false);
    setIsUserManagementDialogOpen(false);
  };

  // for userManagement Dialog
  const handleUserManagementDialogClose = () => {
    setIsUserManagementDialogOpen(false);
  };
  const handleUserManagementDialog = () => {
    setIsMenuVisible(false);
    setIsUserManagementDialogOpen(true);
    setIsConfirmDeleteAccountDialogOpen(false);
    setIsCustomerProfileDialogOpen(false);
  };

  // for userProfile Dialog
  const handleUserProfileDialogClose = () => {
    setIsUserProfileDialogOpen(false);
  };
  const handleUserProfileDialog = () => {
    setIsMenuVisible(false);
    setIsUserProfileDialogOpen(true);
  };


  return (
    <div ref={menuRef}>
      <Wrapper>
        <User>
          <CompanyName onClick={handleToggleMenu}>
            <span className="company">
              {companyName ? companyName : <LoadingIndicator type={"TEXT"} length={10} />}
            </span>

            <ExpandMoreRoundedIcon fontSize={isMobile ? "small" : "medium"} />
          </CompanyName>
          <UserName>
            {userName ? userName : <LoadingIndicator type={"TEXT"} length={10} />}
          </UserName>

          {isMenuVisible && (
            <FadeIn>
              <ListWrapper>
                <List>
                  {currentUser.user.root && (
                    <ListItem onClick={handleCustomerProfileDialog}>
                      {t("top_bar.customer_profile_dialog.headline")}
                    </ListItem>
                  )}
                  {currentUser.user.root && (
                    <ListItem onClick={handleUserManagementDialog}>
                      {t("top_bar.user_management_dialog.headline")}
                    </ListItem>
                  )}
                  {currentUser.user.root && (
                    <ListItem onClick={handleAccountDeleteDialog}>
                      {t("top_bar.account_dialog.delete_account", {})}
                    </ListItem>
                  )}
                  {!currentUser.user.root && (
                    <ListItem onClick={handleUserProfileDialog}>
                      {t("top_bar.user_profile_dialog.headline")}
                    </ListItem>
                  )}
                  <ListItem onClick={handleLogOut}>{t("top_bar.logout")}</ListItem>
                </List>
              </ListWrapper>
            </FadeIn>
          )}
        </User>
      </Wrapper>

      {/* Dialog for Customer Profile */}
      <CustomerProfileDialog
        dialogOpen={isCustomerProfileDialogOpen}
        handleDialogClose={handleCustomerProfileDialogClose}
        product={TConfig.defaults.PRODUCT_ID}
      />
      {/* Dialog for User Management */}
      <UserManagementDialog
        dialogOpen={isUserManagementDialogOpen}
        handleDialogClose={handleUserManagementDialogClose}
      />
      {/* Dialog for User Profile */}
      <UserProfileDialog
        dialogOpen={isUserProfileDialogOpen}
        handleDialogClose={handleUserProfileDialogClose}
      />
      {/* Dialog for account deletion */}

      <SecondaryDialog
        type="warning"
        dialogTitle={t("top_bar.account_dialog.delete_account")}
        contentText={
          <Trans
            i18nKey="top_bar.account_dialog.deleteDialog_content_Text"
            values={{ companyName }}
          />
        }
        secondaryDialogOpen={isConfirmDeleteAccountDialogOpen}
        secondaryDialogClose={handleDeleteDialogClose}
        eventHandler={handleDeleteAccount}
        actionButtonText={t("platform/common:misc.delete")}
        isMenuVisible={isMenuVisible}
      />
    </div>
  );
};

export default UserMenu;
